/**
 * PublicGoalOverlay — PMA-COCK-187. A centred, iOS-live-activity-style card
 * announcing a goal on the public share page (`PublicMatchAnalytics.tsx`),
 * client request 2026-09-12 ("que salga en el centro con el escudo de quien
 * hizo el gol"), `specs/2026-09-13-analytics-period-maps-and-goal-overlay-design.md`.
 * Public-share-page only — the cockpit keeps its current live-event
 * behaviour unchanged (locked decision 1). A goal no longer produces a
 * corner toast at all: see `PublicEventToast.tsx`'s `TOAST_SUPPRESSED_KINDS`,
 * which diverts goal-classified events away from that queue so this overlay
 * is the sole live announcement of a goal on the public page.
 *
 * **Generalised to cards (2026-09-24, AC8, "The Shot Flow Still Leaves the
 * Pitch...").** The client asked for the same kind of popup for a yellow or
 * red card ("que salga igual que el gol"). `GoalOverlayItem.kind` now
 * distinguishes `"goal" | "yellowCard" | "redCard"`: a card swaps the amber
 * "GOL" title and crest for a card glyph and a yellow/red accent, keeps the
 * player + minute line, and drops the score line entirely (a card doesn't
 * change the score). `TOAST_SUPPRESSED_KINDS` (`PublicEventToast.tsx`) now
 * also suppresses `yellowCard`/`redCard` so a card is never announced
 * twice; `PublicMatchAnalytics.tsx`'s `processEventsForToasts` pushes card
 * items into this same queue, merging a "Yellow (Second)" and its
 * auto-paired "Red" into ONE red popup (mirrors
 * `publicHeaderEvents.ts`'s `derivePublicTeamCards` merge).
 *
 * One card at a time: `useGoalOverlayQueue` holds at most one `active` item,
 * queuing any item that arrives while a card is already showing and
 * promoting the next one the render after the current one retires — no
 * stagger delay, unlike `PublicEventToast.tsx`'s corner queue, since only
 * one card is ever on screen here.
 *
 * **Crest restored alongside the glyph (2026-09-25, client wave, "Colocarle
 * el escudo del equipo... no van a saber de qué equipo es").** This reverses
 * the 2026-09-24 change above that swapped the crest for a bare card glyph —
 * a card popup now shows BOTH the carded team's `TeamLogo` and the glyph,
 * side by side. A goal popup is unaffected (crest only, no glyph, unchanged).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import type { MouseEvent } from "react";
import TeamLogo from "../TeamLogo";
import type { Team } from "../../pages/logger/types";
import { useMediaQuery } from "../../hooks/useMediaQuery";

export type PublicOverlayKind = "goal" | "yellowCard" | "redCard";

export interface GoalOverlayItem {
  /** Same key shape as PublicEventToast's `eventToastKey` — the id of the
   * scoring/carded event, so an item can never double-queue itself. */
  id: string;
  /** AC8: which popup this is — drives the title, glyph and accent. */
  kind: PublicOverlayKind;
  /** The team the goal is credited to, or the carded player's own team. */
  teamId: string;
  /** The team object `TeamLogo` renders (that team's own crest). */
  crestTeam: Team;
  /** e.g. "J. Ramirez"; "" when the scorer/carded player is unknown. */
  playerLabel: string;
  /** e.g. "34'". */
  minuteLabel: string;
  /** e.g. "MPZ 2 - 1 ASC". Omitted for a card — AC8: "no score line for
   * cards", a card never changes the score. */
  scoreLabel?: string;
}

const AUTO_DISMISS_MS = 5000;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** AC8 — fixed, meaning-carrying colours for the card popup's glyph/accent,
 * matching the cockpit's own yellow/red card convention elsewhere in the
 * app (`CardChips` in `PublicTeamSheet.tsx`: `bg-yellow-400`/`bg-red-500`). */
const CARD_ACCENT: Record<"yellowCard" | "redCard", string> = {
  yellowCard: "#facc15",
  redCard: "#ef4444",
};

/**
 * A small rounded playing-card shape — AC8's "card glyph", replacing the
 * crest+amber-"GOL" pairing for a card popup. Deliberately not a photo-real
 * card icon; a flat rounded rect in the card's own colour reads instantly
 * at this size, the same "flat, meaning-carrying shape" approach
 * `GoalMouthMap.tsx`'s `SHOT_MARKER_COLOR` legend already uses.
 */
function CardGlyph({ color }: { color: string }) {
  return (
    <svg
      viewBox="0 0 24 32"
      className="h-14 w-10"
      aria-hidden="true"
      data-testid="public-goal-overlay-card-glyph"
    >
      <rect
        x="1.5"
        y="1.5"
        width="21"
        height="29"
        rx="3.5"
        fill={color}
        stroke="#0f172a"
        strokeWidth="2"
      />
    </svg>
  );
}

/**
 * One-item queue: `enqueue()` appends new goal items; `active` is always
 * either null or the single item currently on screen. The head of the
 * internal queue is promoted into `active` the moment `active` is null and
 * the queue is non-empty — on first enqueue that is immediate (no card was
 * showing), and again every time `dismiss()` clears the current one.
 */
export function useGoalOverlayQueue(): {
  active: GoalOverlayItem | null;
  enqueue: (items: GoalOverlayItem[]) => void;
  dismiss: () => void;
} {
  const [queue, setQueue] = useState<GoalOverlayItem[]>([]);
  const [active, setActive] = useState<GoalOverlayItem | null>(null);

  const enqueue = useCallback((items: GoalOverlayItem[]) => {
    if (items.length === 0) return;
    setQueue((prev) => [...prev, ...items]);
  }, []);

  const dismiss = useCallback(() => {
    setActive(null);
  }, []);

  useEffect(() => {
    if (active !== null || queue.length === 0) return;
    const [next, ...rest] = queue;
    setActive(next);
    setQueue(rest);
  }, [active, queue]);

  return { active, enqueue, dismiss };
}

interface PublicGoalOverlayProps {
  item: GoalOverlayItem | null;
  onDismiss: () => void;
  t: TFunction;
}

export default function PublicGoalOverlay({
  item,
  onDismiss,
  t,
}: PublicGoalOverlayProps) {
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  // onDismiss has no guaranteed stable identity across renders (the public
  // page re-renders on its own clock tick) — a ref keeps the timer/keydown
  // effects below keyed only on the item's own id, same rationale as
  // PublicEventToast.tsx's PublicEventToastItem convention.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  // Starts false so the fade+scale transition actually animates in on the
  // frame after the card mounts, rather than snapping straight to rest —
  // same double-render-via-effect technique as QuickActionMenu.tsx.
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!item) return undefined;
    setEntered(false);
    const raf = requestAnimationFrame(() => setEntered(true));
    const dismissTimer = setTimeout(() => {
      onDismissRef.current();
    }, AUTO_DISMISS_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(dismissTimer);
    };
  }, [item?.id]);

  useEffect(() => {
    if (!item) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismissRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [item?.id]);

  const handleDismissButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDismiss();
    },
    [onDismiss],
  );

  if (!item) return null;

  const animate = !prefersReducedMotion;
  const transitionClass = animate ? "transition-all duration-300 ease-out" : "";
  const stateClass =
    animate && !entered ? "opacity-0 scale-90" : "opacity-100 scale-100";

  // AC8: kind drives the title text, its accent colour, and whether a card
  // glyph replaces the crest — everything else (dismiss/timer/keyboard
  // behaviour, player + minute line) is shared, unchanged, across all three
  // kinds.
  const isCard = item.kind === "yellowCard" || item.kind === "redCard";
  const titleText =
    item.kind === "goal"
      ? t("goalOverlay.title", "GOAL")
      : item.kind === "yellowCard"
        ? t("cardOverlay.yellowTitle", "YELLOW CARD")
        : t("cardOverlay.redTitle", "RED CARD");
  const titleColorClass =
    item.kind === "goal"
      ? "text-amber-400"
      : item.kind === "yellowCard"
        ? "text-yellow-400"
        : "text-red-500";
  const ringClass = isCard
    ? item.kind === "yellowCard"
      ? "ring-2 ring-yellow-400/60"
      : "ring-2 ring-red-500/60"
    : "ring-1 ring-white/10";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onDismiss}
    >
      <div
        role="status"
        aria-live="polite"
        data-testid="public-goal-overlay"
        data-overlay-kind={item.kind}
        className={`relative flex w-full max-w-xs flex-col items-center gap-2 rounded-2xl bg-slate-900/95 px-8 py-6 text-center shadow-2xl ${ringClass} ${transitionClass} ${stateClass}`}
      >
        <button
          type="button"
          data-testid="public-goal-overlay-dismiss"
          aria-label={t("goalOverlay.dismiss", "Dismiss")}
          onClick={handleDismissButtonClick}
          // ui-judge round-1 backlog item: was p-1 (16px icon + 8px padding
          // = 24x24 total, measured touchTargets 24x24). p-3.5 (14px each
          // side) makes the icon's own 16x16 box exactly 44x44 total —
          // the icon itself is untouched, only the button's own hit area
          // grows.
          className="absolute right-2 top-2 rounded-full p-3.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {isCard ? (
          <div className="flex items-center gap-3">
            <TeamLogo team={item.crestTeam} size="xl" bare />
            <CardGlyph
              color={CARD_ACCENT[item.kind as "yellowCard" | "redCard"]}
            />
          </div>
        ) : (
          <TeamLogo team={item.crestTeam} size="xl" bare />
        )}
        <p
          className={`text-2xl font-extrabold uppercase tracking-wide ${titleColorClass}`}
        >
          {titleText}
        </p>
        <p className="text-base font-semibold text-slate-100">
          {item.playerLabel
            ? `${item.playerLabel} · ${item.minuteLabel}`
            : item.minuteLabel}
        </p>
        {item.scoreLabel && (
          <p className="font-mono text-sm text-slate-300">{item.scoreLabel}</p>
        )}
      </div>
    </div>
  );
}
